import React, { useCallback, useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import {
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilSquareIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useTable, usePagination } from "react-table";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical, faUserMd, faPlus } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";

const ACTION_MENU_WIDTH = 288;
const ACTION_MENU_HEIGHT = 320;
const ACTION_MENU_MARGIN = 12;
const ACTION_MENU_GAP = 8;

const getActionMenuPosition = (triggerElement) => {
  const rect = triggerElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = rect.right - ACTION_MENU_WIDTH;
  left = Math.max(
    ACTION_MENU_MARGIN,
    Math.min(left, viewportWidth - ACTION_MENU_WIDTH - ACTION_MENU_MARGIN)
  );

  let top = rect.bottom + ACTION_MENU_GAP;
  let placement = "bottom";

  if (top + ACTION_MENU_HEIGHT > viewportHeight - ACTION_MENU_MARGIN) {
    top = rect.top - ACTION_MENU_HEIGHT - ACTION_MENU_GAP;
    placement = "top";
  }

  const maxTop = Math.max(
    ACTION_MENU_MARGIN,
    viewportHeight - ACTION_MENU_HEIGHT - ACTION_MENU_MARGIN
  );
  top = Math.max(ACTION_MENU_MARGIN, Math.min(top, maxTop));

  return { top, left, placement };
};

const getRowActionKey = (physician) => String(physician?.physician_id || "");

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    red: "bg-rose-100 text-rose-700 ring-rose-200",
    yellow: "bg-amber-100 text-amber-700 ring-amber-200",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const EmptyState = ({
  title = "No results",
  subtitle = "Try adjusting your search or filters.",
}) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
    <svg
      viewBox="0 0 24 24"
      className="mb-2 h-8 w-8 text-gray-400"
      fill="none"
      stroke="currentColor"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
    <div className="text-sm font-medium text-gray-900">{title}</div>
    <div className="text-xs text-gray-500">{subtitle}</div>
  </div>
);

const PhysicianTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("physician_id");
  const [sortDirection, setSortDirection] = useState("asc");

  const [accreditationFilter, setAccreditationFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const [selectedPhysicianMovement, setSelectedPhysicianMovement] = useState(null);
  const [movementData, setMovementData] = useState([]);
  const [movementLoading, setMovementLoading] = useState(false);
  const [movementCurrentPage, setMovementCurrentPage] = useState(1);
  const [movementItemsPerPage, setMovementItemsPerPage] = useState(5);
  const [movementTotalCount, setMovementTotalCount] = useState(0);

  const [accreditationModalOpen, setAccreditationModalOpen] = useState(false);
  const [selectedPhysician, setSelectedPhysician] = useState(null);
  const [newAccreditationStatus, setNewAccreditationStatus] = useState("0");
  const [remarks, setRemarks] = useState("");
  const [savingAccreditation, setSavingAccreditation] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  const fetchPhysicians = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/physicians`, {
        headers: getAuthHeaders(),
        params: {
          limit_offset: (currentPage - 1) * itemsPerPage,
          search_string: searchQuery,
          sort_by: sortBy,
          sort_direction: sortDirection,
          limit: itemsPerPage,
          accreditation_flag: accreditationFilter === "ALL" ? undefined : accreditationFilter,
          status_flag: statusFilter === "ALL" ? undefined : statusFilter,
        },
      });
      setData(response.data.physicians || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      toast.error("Failed to fetch physicians.", { autoClose: 3000 });
      setTimeout(() => navigate("/"), 2000);
    } finally {
      setLoading(false);
    }
  };

  const fetchPhysicianMovement = async (physicianCode) => {
    setMovementLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/physicians/${physicianCode}/movement`,
        {
          headers: getAuthHeaders(),
          params: {
            page: movementCurrentPage,
            per_page: movementItemsPerPage,
            sort_by: "lastupdate_date",
            sort_direction: "desc",
          },
        }
      );

      setMovementData(response.data.data.movements || response.data.data.batches || []);
      setMovementTotalCount(response.data.data.total || 0);
    } catch (error) {
      toast.error("Failed to fetch physician movement.", { autoClose: 3000 });
    } finally {
      setMovementLoading(false);
    }
  };

  useEffect(() => {
    fetchPhysicians();
  }, [currentPage, itemsPerPage, searchQuery, sortBy, sortDirection, accreditationFilter, statusFilter]);

  useEffect(() => {
    if (selectedPhysicianMovement?.physician_id) {
      fetchPhysicianMovement(selectedPhysicianMovement.physician_id);
    }
  }, [selectedPhysicianMovement, movementCurrentPage, movementItemsPerPage]);

  const accreditationStatusMap = {
    0: { name: "Accredited", color: "green" },
    1: { name: "Disaccredited", color: "red" },
    2: { name: "Non-accredited", color: "yellow" },
    3: { name: "N/A", color: "gray" },
    default: { name: "Unknown", color: "gray" },
  };

  const statusMap = {
    0: { name: "Not Accepting", color: "red" },
    1: { name: "Accepting", color: "green" },
    2: { name: "Terminated/Cancelled", color: "slate" },
    3: { name: "Suspended", color: "yellow" },
    5: { name: "Extension", color: "blue" },
    4: { name: "N/A", color: "gray" },
    default: { name: "Unknown", color: "gray" },
  };

  const closeActionMenu = useCallback(() => {
    setOpenActionMenu(null);
    setActionMenuPosition(null);
  }, []);

  const handleActionMenuToggle = useCallback(
    (physician, event) => {
      event.stopPropagation();
      const rowKey = getRowActionKey(physician);

      if (openActionMenu === rowKey) {
        closeActionMenu();
        return;
      }

      setOpenActionMenu(rowKey);
      setActionMenuPosition(getActionMenuPosition(event.currentTarget));
    },
    [closeActionMenu, openActionMenu]
  );

  useEffect(() => {
    if (!openActionMenu) {
      return undefined;
    }

    const handleViewportChange = () => closeActionMenu();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [closeActionMenu, openActionMenu]);

  const getRowActions = (physician) => {
    const isAccredited = String(physician?.accreditation_flag) === "0";

    return [
      {
        key: "view",
        label: "View physician details",
        Icon: EyeIcon,
        color: "text-blue-600",
        onClick: () => navigate(`/physician/view/${physician.physician_id}`),
      },
      {
        key: "edit",
        label: "Edit physician details",
        Icon: PencilSquareIcon,
        color: "text-amber-600",
        onClick: () => navigate(`/physician/edit/${physician.physician_id}`),
      },
      {
        key: "movement",
        label: "View physician movement",
        Icon: ClockIcon,
        color: "text-purple-600",
        onClick: () => {
          setMovementCurrentPage(1);
          setSelectedPhysicianMovement(physician);
          setMovementModalOpen(true);
        },
      },
      {
        key: "accreditation",
        label: isAccredited ? "Disaccredit physician" : "Accredit physician",
        Icon: isAccredited ? XCircleIcon : CheckCircleIcon,
        color: isAccredited ? "text-rose-600" : "text-emerald-600",
        onClick: () => openAccreditationModal(physician),
      },
    ];
  };

  const columns = useMemo(
    () => [
      {
        Header: "Physician ID",
        accessor: "physician_id",
        Cell: ({ value }) => <span className="font-medium text-gray-900">{value || "N/A"}</span>,
      },
      {
        Header: "Last Name",
        accessor: "last_name",
        Cell: ({ value }) => <span className="text-gray-700">{value || "N/A"}</span>,
      },
      {
        Header: "First Name",
        accessor: "first_name",
        Cell: ({ value }) => <span className="text-gray-700">{value || "N/A"}</span>,
      },
      {
        Header: "Middle Name",
        accessor: "middle_name",
        Cell: ({ value }) => <span className="text-gray-700">{value || "N/A"}</span>,
      },
      {
        Header: "Specializations",
        accessor: "specialization",
        Cell: ({ value }) => <span className="text-gray-600">{value || "N/A"}</span>,
      },
      {
        Header: "Vatable",
        accessor: "vatable",
        Cell: ({ value }) => (
          <Badge color={String(value) === "1" ? "blue" : "gray"}>
            {String(value) === "1" ? "True" : "False"}
          </Badge>
        ),
      },
      {
        Header: "Accreditation Status",
        accessor: "accreditation_flag",
        Cell: ({ value }) => {
          const status = accreditationStatusMap[value] || accreditationStatusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Status",
        accessor: "status_flag",
        Cell: ({ value }) => {
          const status = statusMap[value] || statusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const physician = row.original;
          const rowKey = getRowActionKey(physician);

          return (
            <div className="relative whitespace-nowrap">
              <button
                type="button"
                aria-label={`Actions for physician ${physician.physician_id || ""}`}
                onClick={(event) => handleActionMenuToggle(physician, event)}
                className={clsx(
                  "inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
                  openActionMenu === rowKey
                    ? "border-blue-400 bg-blue-50 text-blue-700 shadow-blue-100"
                    : "border-gray-300 bg-white text-gray-700 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                )}
              >
                <FontAwesomeIcon
                  icon={faEllipsisVertical}
                  className="text-gray-500"
                />
                <span>Actions</span>
              </button>
              {openActionMenu === rowKey &&
                actionMenuPosition &&
                typeof document !== "undefined" &&
                createPortal(
                  <>
                    <button
                      type="button"
                      aria-label="Close physician actions menu"
                      className="fixed inset-0 z-[9998] cursor-default bg-transparent"
                      onClick={closeActionMenu}
                    />
                    <div
                      className="fixed z-[9999] w-72 overflow-visible"
                      style={{
                        top: `${actionMenuPosition.top}px`,
                        left: `${actionMenuPosition.left}px`,
                      }}
                    >
                      <div
                        className={clsx(
                          "absolute right-6 h-3 w-3 rotate-45 border bg-white",
                          actionMenuPosition.placement === "top"
                            ? "-bottom-1.5 border-l-0 border-t-0 border-gray-200"
                            : "-top-1.5 border-b-0 border-r-0 border-gray-200"
                        )}
                      />
                      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl shadow-slate-900/15 ring-1 ring-black/5">
                        <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Physician Actions
                          </p>
                        </div>
                        <div className="space-y-1 p-2">
                          {getRowActions(physician).map((action) => (
                            <button
                              key={action.key}
                              type="button"
                              onClick={() => {
                                action.onClick();
                                closeActionMenu();
                              }}
                              className="group flex w-full items-center rounded-lg border border-transparent px-3 py-2.5 text-left text-sm text-gray-700 transition-all duration-150 hover:translate-x-0.5 hover:border-blue-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-700 hover:shadow-sm"
                            >
                              <span className="mr-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 transition-all duration-150 group-hover:bg-white group-hover:shadow-sm">
                                <action.Icon
                                  className={clsx("h-5 w-5", action.color)}
                                />
                              </span>
                              <span className="truncate font-medium">
                                {action.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
            </div>
          );
        },
      },
    ],
    [
      actionMenuPosition,
      closeActionMenu,
      handleActionMenuToggle,
      openActionMenu,
    ]
  );

  const movementColumns = useMemo(
    () => [
      {
        Header: "Update For",
        accessor: "physician_flag",
        Cell: ({ value }) => {
          const flag = String(value ?? "").toLowerCase();
          const mapping = {
            reactivation: { label: "Activation/Reactivation", color: "green" },
            termination: { label: "Termination/Cancellation", color: "red" },
            suspension: { label: "Suspension", color: "yellow" },
            extension: { label: "Extension", color: "slate" },
            bank_update: { label: "Bank Update", color: "blue" },
          };
          const status = mapping[flag] || { label: "N/A", color: "gray" };
          return <Badge color={status.color}>{status.label}</Badge>;
        },
      },
      {
        Header: "Effective Date",
        accessor: "effectivity_date",
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleDateString("en-PH") : "N/A",
      },
      {
        Header: "Updated By",
        accessor: "update_by",
        Cell: ({ value }) => <span className="text-gray-700">{value || "N/A"}</span>,
      },
      {
        Header: "Reason",
        accessor: "reason",
        Cell: ({ value }) => <span className="text-gray-700">{value || "N/A"}</span>,
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: ({ value }) => <span className="text-gray-700">{value || "N/A"}</span>,
      },
      {
        Header: "Last Update",
        accessor: "lastupdate_date",
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleString("en-PH") : "N/A",
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: currentPage - 1, pageSize: itemsPerPage },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / itemsPerPage),
    },
    usePagination
  );

  const {
    getTableProps: getMovementTableProps,
    getTableBodyProps: getMovementTableBodyProps,
    headerGroups: movementHeaderGroups,
    rows: movementRows,
    prepareRow: prepareMovementRow,
  } = useTable(
    {
      columns: movementColumns,
      data: movementData,
      initialState: { pageIndex: movementCurrentPage - 1, pageSize: movementItemsPerPage },
      manualPagination: true,
      pageCount: Math.ceil(movementTotalCount / movementItemsPerPage),
    },
    usePagination
  );

  const handleSort = (column) => {
    if (!column || !column.id || column.disableSortBy) return;
    if (sortBy === column.id) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column.id);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const movementTotalPages = Math.max(1, Math.ceil(movementTotalCount / movementItemsPerPage));
  const getMovementPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, movementCurrentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(movementTotalPages, startPage + maxPagesToShow - 1);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const openAccreditationModal = (physician) => {
    setSelectedPhysician(physician);
    setNewAccreditationStatus(physician.accreditation_flag === "0" ? "1" : "0");
    setRemarks("");
    setAccreditationModalOpen(true);
  };

  const closeAccreditationModal = () => {
    setAccreditationModalOpen(false);
    setSelectedPhysician(null);
    setRemarks("");
  };

  const handleSubmitAccreditation = async () => {
    if (!selectedPhysician) return;
    if (newAccreditationStatus === "1" && !remarks.trim()) {
      toast.error("Remarks are required when disaccrediting.");
      return;
    }
    setSavingAccreditation(true);
    try {
      await axios.put(
        `${BASE_URL}/physicians/${selectedPhysician.physician_id}/accreditation`,
        {
          accreditation_flag: newAccreditationStatus,
          remarks,
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`Physician ${newAccreditationStatus === "0" ? "accredited" : "disaccredited"} successfully.`);
      fetchPhysicians();
      closeAccreditationModal();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update accreditation status.");
    } finally {
      setSavingAccreditation(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-1xl p-4">
      <ToastContainer />

      {/* Toolbar */}
      <div className="mb-4 rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-white/10 p-2 ring-1 ring-white/20 backdrop-blur">
              <FontAwesomeIcon icon={faUserMd} className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-semibold">Physicians (Affiliations)</h1>
              <div className="text-xs text-white/90">
                {totalCount.toLocaleString()} total record{totalCount === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate("/physician/add")}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/20 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Add New Physician</span>
            </button>
            <button
              onClick={() => navigate("/physician-batch-upload")}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/20 hover:bg-white/20"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>Physician Batch Upload</span>
            </button>
          </div>
        </div>

        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search physicians..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <select
            value={accreditationFilter}
            onChange={(e) => {
              setAccreditationFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-md border border-gray-200 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="ALL">All Accreditation Statuses</option>
            <option value="0">Accredited</option>
            <option value="1">Disaccredited</option>
            <option value="2">Non-accredited</option>
            {/* <option value="3">N/A</option> */}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-md border border-gray-200 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="ALL">All Statuses</option>
            <option value="0">Not Accepting</option>
            <option value="1">Accepting</option>
            <option value="2">Terminated/Cancelled</option>
            <option value="3">Suspended</option>
            <option value="5">Extension</option>
            {/* <option value="4">N/A</option> */}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="max-h-[60vh] overflow-auto">
          {loading ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-gray-50">
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        key={column.id}
                        {...column.getHeaderProps()}
                        onClick={() => handleSort(column)}
                        className={clsx(
                          "px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600",
                          "cursor-pointer select-none"
                        )}
                      >
                        <div className="inline-flex items-center gap-1">
                          {column.render("Header")}
                          {column.id === sortBy && (
                            <span className="text-gray-400">
                              {sortDirection === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {[...Array(itemsPerPage)].map((_, idx) => (
                  <tr key={idx} className="animate-pulse border-t">
                    {[...Array(columns.length)].map((__, i) => (
                      <td key={i} className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : data.length === 0 ? (
            <div className="p-8">
              <EmptyState title="No physicians found" subtitle="Try changing your filters or search." />
            </div>
          ) : (
            <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-gray-50">
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        key={column.id}
                        {...column.getHeaderProps()}
                        onClick={() => handleSort(column)}
                        className={clsx(
                          "px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600",
                          "cursor-pointer select-none"
                        )}
                      >
                        <div className="inline-flex items-center gap-1">
                          {column.render("Header")}
                          {column.id === sortBy && (
                            <span className="text-gray-400">
                              {sortDirection === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="divide-y divide-gray-100">
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} className="hover:bg-gray-50 transition-colors">
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()} className="px-4 py-3 text-sm text-gray-800">
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row">
        <div className="text-xs text-gray-600">
          Showing {totalCount ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount.toLocaleString()} entries
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === 1 ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            <ChevronDoubleLeftIcon className="h-5 w-5" />
            First
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === 1 ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            <ChevronLeftIcon className="h-5 w-5" />
            Previous
          </button>
          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={clsx(
                "rounded-md px-2 py-1 text-xs",
                currentPage === page ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === totalPages ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            Next
            <ChevronRightIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === totalPages ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            Last
            <ChevronDoubleRightIcon className="h-5 w-5" />
          </button>

          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Accreditation Modal */}
      {movementModalOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => {
            setMovementModalOpen(false);
            setSelectedPhysicianMovement(null);
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white flex justify-between items-center">
              <h2 className="text-base font-semibold">
                Physician Movement History of {selectedPhysicianMovement?.last_name},{" "}
                {selectedPhysicianMovement?.first_name}
              </h2>
              <button
                onClick={() => {
                  setMovementModalOpen(false);
                  setSelectedPhysicianMovement(null);
                }}
                className="text-white/80 hover:text-white"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 max-h-[60vh] overflow-auto">
              {movementLoading ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    {movementHeaderGroups.map((headerGroup, i) => (
                      <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th
                            key={column.id}
                            {...column.getHeaderProps()}
                            className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                          >
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {[...Array(movementItemsPerPage)].map((_, idx) => (
                      <tr key={idx} className="animate-pulse border-t">
                        {[...Array(movementColumns.length)].map((__, i) => (
                          <td key={i} className="px-4 py-3">
                            <div className="h-4 bg-gray-200 rounded" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : movementData.length === 0 ? (
                <EmptyState title="No movements found" subtitle="No physician movement history available for this physician." />
              ) : (
                <table {...getMovementTableProps()} className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    {movementHeaderGroups.map((headerGroup, i) => (
                      <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th
                            key={column.id}
                            {...column.getHeaderProps()}
                            className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                          >
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getMovementTableBodyProps()} className="divide-y divide-gray-100">
                    {movementRows.map((row) => {
                      prepareMovementRow(row);
                      return (
                        <tr {...row.getRowProps()} className="hover:bg-gray-50 transition-colors">
                          {row.cells.map((cell) => (
                            <td {...cell.getCellProps()} className="px-4 py-3 text-sm text-gray-800">
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row">
              <div className="text-xs text-gray-600">
                Showing {movementTotalCount ? (movementCurrentPage - 1) * movementItemsPerPage + 1 : 0} to {Math.min(movementCurrentPage * movementItemsPerPage, movementTotalCount)} of {movementTotalCount.toLocaleString()} entries
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <button
                  onClick={() => setMovementCurrentPage(1)}
                  disabled={movementCurrentPage === 1}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    movementCurrentPage === 1 ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  <ChevronDoubleLeftIcon className="h-5 w-5" />
                  First
                </button>
                <button
                  onClick={() => setMovementCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={movementCurrentPage === 1}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    movementCurrentPage === 1 ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                  Previous
                </button>
                {getMovementPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => setMovementCurrentPage(page)}
                    className={clsx(
                      "rounded-md px-2 py-1 text-xs",
                      movementCurrentPage === page ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    )}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setMovementCurrentPage((p) => Math.min(movementTotalPages, p + 1))}
                  disabled={movementCurrentPage === movementTotalPages}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    movementCurrentPage === movementTotalPages ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  Next
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setMovementCurrentPage(movementTotalPages)}
                  disabled={movementCurrentPage === movementTotalPages}
                  className={clsx(
                    "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
                    movementCurrentPage === movementTotalPages ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  Last
                  <ChevronDoubleRightIcon className="h-5 w-5" />
                </button>
                <select
                  value={movementItemsPerPage}
                  onChange={(e) => {
                    setMovementItemsPerPage(Number(e.target.value));
                    setMovementCurrentPage(1);
                  }}
                  className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  {[5, 10, 20, 50].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      <AnimatePresence>
        {accreditationModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={closeAccreditationModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
            >
              <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
                <h2 className="text-base font-semibold">
                  Update Accreditation Status for {selectedPhysician?.last_name}, {selectedPhysician?.first_name}
                </h2>
                <div className="text-xs text-white/80">{selectedPhysician?.physician_id}</div>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">New Status *</label>
                  <select
                    value={newAccreditationStatus}
                    onChange={(e) => setNewAccreditationStatus(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="0">Accredited</option>
                    <option value="1">Disaccredited</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Remarks {newAccreditationStatus === "1" ? "*" : "(optional)"}
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={4}
                    placeholder="Enter reason for status change..."
                    className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeAccreditationModal}
                    disabled={savingAccreditation}
                    className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-800 hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitAccreditation}
                    disabled={savingAccreditation}
                    className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    {savingAccreditation ? (
                      <>
                        <svg
                          className="h-5 w-5 animate-spin text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Saving…
                      </>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PhysicianTable;

